import type { FC, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { createAudioContext, createOutput, scheduleKeystroke } from '@/lib/typing-sound'

interface Stroke {
    time: number
    intensity: number
    pitch: number
    space: boolean
}

interface TypingSoundPlayerProps {
    className?: string
}

type PlayerStatus = 'idle' | 'playing' | 'paused'

const DURATION = 6.4
const BAR_COUNT = 88
const SAMPLE_TEXT = 'nano blush linear switches sound soft and smooth at your desk'

const seededRandom = (seed: number) => {
    let state = seed >>> 0

    return (): number => {
        state = (state + 0x6d2b79f5) >>> 0

        let value = state

        value = Math.imul(value ^ (value >>> 15), value | 1)
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61)

        return ((value ^ (value >>> 14)) >>> 0) / 4294967296
    }
}

const buildStrokes = (): Stroke[] => {
    const random = seededRandom(42)
    const strokes: Stroke[] = []

    let time = 0.15

    for (const char of SAMPLE_TEXT) {
        if (time > DURATION - 0.35) break

        const space = char === ' '

        strokes.push({
            time: Number(time.toFixed(3)),
            intensity: space ? 0.95 : 0.6 + random() * 0.4,
            pitch: 0.9 + random() * 0.2,
            space
        })

        time += space ? 0.16 + random() * 0.12 : 0.07 + random() * 0.08
    }

    return strokes
}

const buildBars = (strokes: Stroke[]): number[] => {
    const random = seededRandom(7)
    const bars = Array.from({ length: BAR_COUNT }, () => 0.1 + random() * 0.12)

    for (const stroke of strokes) {
        const index = Math.floor((stroke.time / DURATION) * BAR_COUNT)
        const peak = stroke.space ? 1 : 0.5 + stroke.intensity * 0.45
        const shape = [peak, peak * 0.55, peak * 0.28]

        shape.forEach((value, offset) => {
            const target = index + offset

            if (target < BAR_COUNT) bars[target] = Math.max(bars[target], value)
        })
    }

    return bars.map(value => Math.round(Math.min(1, value) * 100))
}

const STROKES = buildStrokes()
const BARS = buildBars(STROKES)

const TypingSoundPlayer: FC<TypingSoundPlayerProps> = ({ className }): ReactNode => {
    const [status, setStatus] = useState<PlayerStatus>('idle')
    const [played, setPlayed] = useState(0)
    const [unsupported, setUnsupported] = useState(false)
    const contextRef = useRef<AudioContext | null>(null)
    const startRef = useRef(0)
    const frameRef = useRef(0)

    const tick = useCallback(() => {
        const context = contextRef.current

        if (!context) return

        const elapsed = context.currentTime - startRef.current

        if (elapsed >= DURATION) {
            setStatus('idle')
            setPlayed(0)
            return
        }

        setPlayed(Math.max(0, Math.floor((elapsed / DURATION) * BAR_COUNT)))
        frameRef.current = requestAnimationFrame(tick)
    }, [])

    useEffect(() => {
        return () => {
            cancelAnimationFrame(frameRef.current)
            void contextRef.current?.close()
        }
    }, [])

    const toggle = async () => {
        const current = contextRef.current

        if (status === 'playing' && current) {
            cancelAnimationFrame(frameRef.current)
            setStatus('paused')
            await current.suspend()
            return
        }

        if (status === 'paused' && current) {
            await current.resume()
            setStatus('playing')
            frameRef.current = requestAnimationFrame(tick)
            return
        }

        const context = current ?? createAudioContext()

        if (!context) {
            setUnsupported(true)
            return
        }

        contextRef.current = context

        if (context.state === 'suspended') await context.resume()

        const output = createOutput(context, 0.8)
        const start = context.currentTime + 0.06

        startRef.current = start

        for (const stroke of STROKES) {
            scheduleKeystroke(context, output, start + stroke.time, stroke)
        }

        cancelAnimationFrame(frameRef.current)
        setPlayed(0)
        setStatus('playing')
        frameRef.current = requestAnimationFrame(tick)
    }

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <div className='flex items-center gap-4'>
                <button
                    type='button'
                    onClick={() => void toggle()}
                    aria-label={status === 'playing' ? 'Pause the switch sound sample' : 'Play the switch sound sample'}
                    className='grid size-7 shrink-0 place-items-center rounded-full bg-white text-[#0b0b0d] transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white active:scale-95'
                >
                    {status === 'playing' ? <Pause className='size-3 fill-current' /> : <Play className='ml-0.5 size-3 fill-current' />}
                </button>

                <div aria-hidden='true' className='flex h-8 min-w-0 flex-1 items-center gap-[2.5px] overflow-hidden'>
                    {BARS.map((height, index) => (
                        <span
                            key={index}
                            className={cn(
                                'w-px shrink-0 rounded-full transition-colors duration-150',
                                index < played ? 'bg-white' : 'bg-white/25'
                            )}
                            style={{ height: `${height}%` }}
                        />
                    ))}
                </div>
            </div>

            {unsupported && (
                <p role='status' className='text-[13px] text-[#77787c]'>
                    Sound playback is not available in this browser.
                </p>
            )}
        </div>
    )
}

export default TypingSoundPlayer
